/**
 * Morphing Gradient Background Animation
 * Część 1: Klasa SimplexNoise
 */

const morphingGradientsBackground = () => {
  const F3 = 1 / 3,
    G3 = 1 / 6;

  // Zoptymalizowana klasa SimplexNoise
  class SimplexNoise {
    constructor(seed) {
      const random =
        typeof seed === "function"
          ? seed
          : seed
          ? this.alea(seed)
          : Math.random;
      this.p = this.buildPermutationTable(random);
      this.perm = new Uint8Array(512);
      this.permMod12 = new Uint8Array(512);
      for (let i = 0; i < 512; i++) {
        this.perm[i] = this.p[i & 255];
        this.permMod12[i] = this.perm[i] % 12;
      }
    }

    grad3 = new Float32Array([
      1, 1, 0, -1, 1, 0, 1, -1, 0, -1, -1, 0, 1, 0, 1, -1, 0, 1, 1, 0, -1, -1,
      0, -1, 0, 1, 1, 0, -1, 1, 0, 1, -1, 0, -1, -1,
    ]);

    buildPermutationTable(random) {
      const p = new Uint8Array(256);
      for (let i = 0; i < 256; i++) p[i] = i;
      for (let i = 0; i < 255; i++) {
        const r = i + ~~(random() * (256 - i));
        [p[i], p[r]] = [p[r], p[i]]; // Zamiana wartości
      }
      return p;
    }

    alea(seed) {
      let s0 = 0,
        s1 = 0,
        s2 = 0,
        c = 1;

      // Prosta funkcja hashująca
      const mash = (data) => {
        let n = 0xefc8249d;
        data = String(data);
        for (let i = 0; i < data.length; i++) {
          n += data.charCodeAt(i);
          let h = 0.02519603282416938 * n;
          n = h >>> 0;
          h -= n;
          h *= n;
          n = h >>> 0;
          h -= n;
          n += h * 0x100000000;
        }
        return (n >>> 0) * 2.3283064365386963e-10;
      };

      s0 = mash(" ");
      s1 = mash(" ");
      s2 = mash(" ");

      if (seed) {
        s0 -= mash(seed);
        if (s0 < 0) s0 += 1;
        s1 -= mash(seed);
        if (s1 < 0) s1 += 1;
        s2 -= mash(seed);
        if (s2 < 0) s2 += 1;
      }

      return () => {
        const t = 2091639 * s0 + c * 2.3283064365386963e-10;
        s0 = s1;
        s1 = s2;
        return (s2 = t - (c = t | 0));
      };
    }

    noise3D(xin, yin, zin) {
      const { permMod12, perm, grad3 } = this;
      const s = (xin + yin + zin) * F3;
      const i = Math.floor(xin + s);
      const j = Math.floor(yin + s);
      const k = Math.floor(zin + s);
      const t = (i + j + k) * G3;
      const X0 = i - t,
        Y0 = j - t,
        Z0 = k - t;
      const x0 = xin - X0,
        y0 = yin - Y0,
        z0 = zin - Z0;

      // Ustalenie kolejności narożników simpleksu
      let i1, j1, k1, i2, j2, k2;
      if (x0 >= y0) {
        if (y0 >= z0) {
          i1 = 1;
          j1 = 0;
          k1 = 0;
          i2 = 1;
          j2 = 1;
          k2 = 0;
        } else if (x0 >= z0) {
          i1 = 1;
          j1 = 0;
          k1 = 0;
          i2 = 1;
          j2 = 0;
          k2 = 1;
        } else {
          i1 = 0;
          j1 = 0;
          k1 = 1;
          i2 = 1;
          j2 = 0;
          k2 = 1;
        }
      } else {
        if (y0 < z0) {
          i1 = 0;
          j1 = 0;
          k1 = 1;
          i2 = 0;
          j2 = 1;
          k2 = 1;
        } else if (x0 < z0) {
          i1 = 0;
          j1 = 1;
          k1 = 0;
          i2 = 0;
          j2 = 1;
          k2 = 1;
        } else {
          i1 = 0;
          j1 = 1;
          k1 = 0;
          i2 = 1;
          j2 = 1;
          k2 = 0;
        }
      }

      // Współrzędne narożników simpleksu
      const x1 = x0 - i1 + G3;
      const y1 = y0 - j1 + G3;
      const z1 = z0 - k1 + G3;
      const x2 = x0 - i2 + 2 * G3;
      const y2 = y0 - j2 + 2 * G3;
      const z2 = z0 - k2 + 2 * G3;
      const x3 = x0 - 1 + 3 * G3;
      const y3 = y0 - 1 + 3 * G3;
      const z3 = z0 - 1 + 3 * G3;

      // Indeksy do tablicy permutacji
      const ii = i & 255,
        jj = j & 255,
        kk = k & 255;

      // Obliczenia wartości szumu dla każdego narożnika
      let n0 = 0,
        n1 = 0,
        n2 = 0,
        n3 = 0;

      let t0 = 0.6 - x0 * x0 - y0 * y0 - z0 * z0;
      if (t0 > 0) {
        const gi0 = permMod12[ii + perm[jj + perm[kk]]] * 3;
        t0 *= t0;
        n0 =
          t0 *
          t0 *
          (grad3[gi0] * x0 + grad3[gi0 + 1] * y0 + grad3[gi0 + 2] * z0);
      }

      let t1 = 0.6 - x1 * x1 - y1 * y1 - z1 * z1;
      if (t1 > 0) {
        const gi1 = permMod12[ii + i1 + perm[jj + j1 + perm[kk + k1]]] * 3;
        t1 *= t1;
        n1 =
          t1 *
          t1 *
          (grad3[gi1] * x1 + grad3[gi1 + 1] * y1 + grad3[gi1 + 2] * z1);
      }

      let t2 = 0.6 - x2 * x2 - y2 * y2 - z2 * z2;
      if (t2 > 0) {
        const gi2 = permMod12[ii + i2 + perm[jj + j2 + perm[kk + k2]]] * 3;
        t2 *= t2;
        n2 =
          t2 *
          t2 *
          (grad3[gi2] * x2 + grad3[gi2 + 1] * y2 + grad3[gi2 + 2] * z2);
      }

      let t3 = 0.6 - x3 * x3 - y3 * y3 - z3 * z3;
      if (t3 > 0) {
        const gi3 = permMod12[ii + 1 + perm[jj + 1 + perm[kk + 1]]] * 3;
        t3 *= t3;
        n3 =
          t3 *
          t3 *
          (grad3[gi3] * x3 + grad3[gi3 + 1] * y3 + grad3[gi3 + 2] * z3);
      }

      return 32 * (n0 + n1 + n2 + n3);
    }
  }

  // Eksport klasy do użycia w drugiej części
  window.SimplexNoise = SimplexNoise;
};

// Wywołaj funkcję aby zainicjalizować SimplexNoise
morphingGradientsBackground();
